import React from 'react';
import { BarChart, TrendingUp, Users, Target, Clock, DollarSign, ArrowUpRight, ArrowDownRight } from 'lucide-react';

const AnalyticsPanel = () => {
  // Datos de ejemplo para las métricas
  const metrics = {
    users: {
      total: 12458,
      active: 8934,
      growth: 23.5,
      retention: 87.2
    },
    revenue: {
      monthly: 45890,
      growth: 18.7,
      arpu: 39.99
    },
    engagement: {
      dailyActive: 3245,
      weeklyActive: 7890,
      averageTime: 28
    },
    tasks: {
      completed: 34567,
      success: 92.3
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-foreground">Analíticas</h1>
        <div className="flex items-center space-x-2">
          <select className="border border-border rounded-lg bg-secondary/30 px-3 py-2 text-sm">
            <option>Últimos 7 días</option>
            <option>Últimos 30 días</option>
            <option>Este mes</option>
            <option>Este año</option>
          </select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="dark-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-foreground/70">Usuarios Activos</p>
              <h3 className="text-2xl font-bold text-foreground mt-1">{metrics.users.active.toLocaleString()}</h3>
            </div>
            <div className="bg-green-100 text-green-800 p-2 rounded-lg flex items-center">
              <ArrowUpRight className="h-5 w-5" />
              <span className="ml-1">{metrics.users.growth}%</span>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-foreground/70">Retención</span>
              <span className="text-green-600">{metrics.users.retention}%</span>
            </div>
            <div className="mt-1 h-2 bg-gray-200 rounded-full">
              <div
                className="h-2 bg-green-500 rounded-full"
                style={{ width: `${metrics.users.retention}%` }}
              ></div>
            </div>
          </div>
        </div>

        <div className="dark-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-foreground/70">Ingresos Mensuales</p>
              <h3 className="text-2xl font-bold text-foreground mt-1">€{metrics.revenue.monthly.toLocaleString()}</h3>
            </div>
            <div className="bg-green-100 text-green-800 p-2 rounded-lg flex items-center">
              <ArrowUpRight className="h-5 w-5" />
              <span className="ml-1">{metrics.revenue.growth}%</span>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-foreground/70">ARPU</span>
              <span className="text-green-600">€{metrics.revenue.arpu}</span>
            </div>
            <div className="mt-1 h-2 bg-gray-200 rounded-full">
              <div
                className="h-2 bg-green-500 rounded-full"
                style={{ width: '75%' }}
              ></div>
            </div>
          </div>
        </div>

        <div className="dark-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-foreground/70">Tareas Completadas</p>
              <h3 className="text-2xl font-bold text-foreground mt-1">{metrics.tasks.completed.toLocaleString()}</h3>
            </div>
            <div className="bg-green-100 text-green-800 p-2 rounded-lg flex items-center">
              <Target className="h-5 w-5" />
              <span className="ml-1">{metrics.tasks.success}%</span>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-foreground/70">Tasa de éxito</span>
              <span className="text-green-600">{metrics.tasks.success}%</span>
            </div>
            <div className="mt-1 h-2 bg-gray-200 rounded-full">
              <div
                className="h-2 bg-green-500 rounded-full"
                style={{ width: `${metrics.tasks.success}%` }}
              ></div>
            </div>
          </div>
        </div>

        <div className="dark-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-foreground/70">Usuarios DAU</p>
              <h3 className="text-2xl font-bold text-foreground mt-1">{metrics.engagement.dailyActive.toLocaleString()}</h3>
            </div>
            <div className="bg-green-100 text-green-800 p-2 rounded-lg flex items-center">
              <Clock className="h-5 w-5" />
              <span className="ml-1">{metrics.engagement.averageTime}m</span>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-foreground/70">WAU</span>
              <span className="text-green-600">{metrics.engagement.weeklyActive.toLocaleString()}</span>
            </div>
            <div className="mt-1 h-2 bg-gray-200 rounded-full">
              <div
                className="h-2 bg-green-500 rounded-full"
                style={{ width: '80%' }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="dark-card p-6">
          <h3 className="text-lg font-medium text-foreground mb-4">Crecimiento de Usuarios</h3>
          <div className="h-64 flex items-center justify-center">
            <p className="text-foreground/70">Gráfico de crecimiento de usuarios</p>
          </div>
        </div>

        <div className="dark-card p-6">
          <h3 className="text-lg font-medium text-foreground mb-4">Ingresos</h3>
          <div className="h-64 flex items-center justify-center">
            <p className="text-foreground/70">Gráfico de ingresos</p>
          </div>
        </div>
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="dark-card p-6">
          <h3 className="text-lg font-medium text-foreground mb-4">Distribución de Usuarios</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-foreground/70">Product Managers</span>
                <span className="text-sm font-medium text-foreground">45%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full">
                <div className="h-2 bg-purple-500 rounded-full" style={{ width: '45%' }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-foreground/70">Desarrolladores</span>
                <span className="text-sm font-medium text-foreground">30%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full">
                <div className="h-2 bg-blue-500 rounded-full" style={{ width: '30%' }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-foreground/70">Emprendedores</span>
                <span className="text-sm font-medium text-foreground">25%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full">
                <div className="h-2 bg-green-500 rounded-full" style={{ width: '25%' }}></div>
              </div>
            </div>
          </div>
        </div>

        <div className="dark-card p-6">
          <h3 className="text-lg font-medium text-foreground mb-4">Distribución de Planes</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-foreground/70">Free</span>
                <span className="text-sm font-medium text-foreground">60%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full">
                <div className="h-2 bg-gray-500 rounded-full" style={{ width: '60%' }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-foreground/70">Pro</span>
                <span className="text-sm font-medium text-foreground">30%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full">
                <div className="h-2 bg-blue-500 rounded-full" style={{ width: '30%' }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-foreground/70">Premium</span>
                <span className="text-sm font-medium text-foreground">10%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full">
                <div className="h-2 bg-purple-500 rounded-full" style={{ width: '10%' }}></div>
              </div>
            </div>
          </div>
        </div>

        <div className="dark-card p-6">
          <h3 className="text-lg font-medium text-foreground mb-4">Métricas de Engagement</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-secondary/30 rounded-lg">
                <p className="text-sm text-foreground/70">Tiempo promedio</p>
                <p className="text-xl font-bold text-foreground">{metrics.engagement.averageTime}m</p>
              </div>
              <div className="p-4 bg-secondary/30 rounded-lg">
                <p className="text-sm text-foreground/70">Tareas/usuario</p>
                <p className="text-xl font-bold text-foreground">12.3</p>
              </div>
              <div className="p-4 bg-secondary/30 rounded-lg">
                <p className="text-sm text-foreground/70">NPS</p>
                <p className="text-xl font-bold text-foreground">72</p>
              </div>
              <div className="p-4 bg-secondary/30 rounded-lg">
                <p className="text-sm text-foreground/70">Churn Rate</p>
                <p className="text-xl font-bold text-foreground">4.8%</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPanel;